import { v2Request } from "../helpers/request";

export type SupademoRoleScope =
  | "BROADCAST"
  | "OFFICE_ONLY"
  | "REPS_ONLY"
  | "DELEGATED_ONLY";

export interface CreateSupademoPayload {
  roleScope: SupademoRoleScope;
  pathPattern: string;
  supademoKey: string;
  title?: string | null;
  isActive?: boolean;
}

// DB/API returns PascalCase keys (per v2 controllers)
export interface SupademoDemoDto {
  Id: number;
  RoleScope: SupademoRoleScope;
  PathPattern: string;
  SupademoKey: string;
  Title: string | null;
  IsActive: boolean;
  CreatedAt: string;
  UpdatedAt: string | null;

  // Engagement fields (present on /supademos/for-user)
  LastOpenedAt?: string | null;
  OpenCount?: number;
  HasOpenedBefore?: 0 | 1; // backend returns 0/1

  // Admin list field (present on /supademos)
  TotalViews?: number;
}

export interface ListSupademosResponse {
  isSuccessful: boolean;
  total: number;
  limit: number;
  offset: number;
  demos: SupademoDemoDto[];
}

export interface ListSupademosForUserResponse {
  isSuccessful: boolean;
  total: number;
  limit: number;
  offset: number;
  demos: SupademoDemoDto[];
}

export const createSupademo = (payload: CreateSupademoPayload) => {
  return v2Request(`/supademos`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
};

export const listSupademos = (params?: { limit?: number; offset?: number }) => {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set("limit", String(params.limit));
  if (params?.offset !== undefined) qs.set("offset", String(params.offset));

  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return v2Request(`/supademos${suffix}`, {
    method: "GET",
  }) as Promise<ListSupademosResponse>;
};

export const listSupademosForUser = (params?: {
  limit?: number;
  offset?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.limit !== undefined) qs.set("limit", String(params.limit));
  if (params?.offset !== undefined) qs.set("offset", String(params.offset));
  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  return v2Request(`/supademos/for-user${suffix}`, {
    method: "GET",
  }) as Promise<ListSupademosForUserResponse>;
};

export type UpdateSupademoPayload = Partial<CreateSupademoPayload>;

export const updateSupademo = (id: number, payload: UpdateSupademoPayload) => {
  return v2Request(`/supademos/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  }) as Promise<JObj>;
};

export interface UpsertUserSupademoEngagementResponse {
  isSuccessful: boolean;
  engagementId?: number;
  demoId?: number;
  userId?: number;
  action?: "created" | "updated";
  error?: string;
}

/**
 * Logs that the current user opened a demo.
 * NOTE: Adjust the endpoint path if your backend route differs.
 */
export const upsertUserSupademoEngagement = (demoId: number) => {
  return v2Request(`/supademos/engagement`, {
    method: "POST",
    body: JSON.stringify({ demoId }),
  }) as Promise<UpsertUserSupademoEngagementResponse>;
};

// -------------------- Admin: demo engagement stats --------------------

export type SupademoEngagementSortBy =
  | "lastOpenedAt"
  | "firstOpenedAt"
  | "openCount"
  | "name"
  | "email";

export type SupademoEngagementSortOrder = "asc" | "desc";

export type SupademoEngagementUserRow = {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
  photo?: string | null;
  firstOpenedAt?: string | null;
  lastOpenedAt?: string | null;
  openCount?: number;
  createdAt?: string;
  updatedAt?: string;
};

export interface SupademoEngagementStatsResponse {
  data: SupademoEngagementUserRow[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

/**
 * Admin-only: users who viewed a demo, with search/sort/pagination.
 * NOTE: adjust endpoint to match your backend route.
 */
export const fetchSupademoEngagementStats = (
  demoId: number,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: SupademoEngagementSortBy;
    sortOrder?: SupademoEngagementSortOrder;
  },
) => {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set("page", String(params.page));
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.search != null) qs.set("search", String(params.search));
  if (params?.sortBy != null) qs.set("sortBy", String(params.sortBy));
  if (params?.sortOrder != null) qs.set("sortOrder", String(params.sortOrder));

  const suffix = qs.toString() ? `?${qs.toString()}` : "";

  // If your backend route differs, change this path:
  // e.g. `/supademos/${demoId}/engagement-stats`
  return v2Request(`/supademos/${demoId}/engagement${suffix}`, {
    method: "GET",
  }) as Promise<SupademoEngagementStatsResponse>;
};
